import { wizardEntries, type WizardValue, type WizardCounts } from '~/features/questions/answer-wizard-model';
import { formatAnswersForChat, type QuestionAnswer, type QuestionDef } from './questions-card';

/**
 * FRONT-3223: the rules behind answering a chat's open question batch from the
 * reply box (eddy, 2026-10-28: "merge questions into the chat box like for the
 * others" — the Builder's panel, FRONT-3155). Pure, because the frontend suite
 * runs under plain node: a rule only a click could prove is a rule nobody
 * proves.
 */

export type ChatAnswerPlan =
  | { kind: 'answers'; answers: QuestionAnswer[]; message: string }
  | { kind: 'plain-message' };

/**
 * What Send does while a batch is open. ONE rule: if any option was chosen or
 * any question skipped, the answers go out — one per question, a skipped or
 * open one as no selection — and the typed text rides along as the person's
 * note; if none was, the typed text is an ordinary message, which already
 * closes the open batch on the server (FRONT-715).
 *
 * The follow-up message is `formatAnswersForChat`'s, unchanged, so every
 * question's text stays in it verbatim (FRONT-2320: an inline skill resumes
 * by finding them), with the note appended after it.
 */
export function planChatAnswerSend(input: { questions: QuestionDef[]; value: WizardValue; text: string }): ChatAnswerPlan {
  const entries = wizardEntries(input.value);
  if (!entries.some((e) => e !== null)) return { kind: 'plain-message' };
  const answers: QuestionAnswer[] = input.questions.map((_, i) => {
    const e = entries[i];
    if (!e || 'skipped' in e) return { selected: [] };
    return { selected: e.selected, ...(e.other ? { other: e.other } : {}) };
  });
  const note = input.text.trim();
  const summary = formatAnswersForChat(input.questions, answers);
  return { kind: 'answers', answers, message: note ? `${summary}\n\nMy note: ${note}` : summary };
}

export interface ChatSendGate {
  disabled: boolean;
  /** Visible text for WHY Send is unavailable — present iff `disabled`. */
  reason?: string;
}

/**
 * Whether Send is available while a batch is open, and if not, why. Disabled
 * iff there is a reason (FRONT-1670), so a new reason cannot ship silent.
 */
export function chatAnswerSendGate(input: {
  counts: Pick<WizardCounts, 'answered' | 'skipped'>;
  typedText: string;
  turnActive: boolean;
}): ChatSendGate {
  let reason: string | undefined;
  if (input.turnActive) {
    reason = 'EDDY is still replying. Answer when it’s done.';
  } else if (input.counts.answered + input.counts.skipped === 0 && input.typedText.trim().length === 0) {
    reason = 'Pick an answer or type one to send.';
  }
  return reason ? { disabled: true, reason } : { disabled: false };
}

/** The reply box's placeholder while a batch is open: where the "nothing chosen, nothing typed" case is said. */
export function chatAnswerPlaceholder(counts: Pick<WizardCounts, 'answered' | 'skipped'>): string {
  return counts.answered + counts.skipped > 0 ? 'Add a note (optional)…' : 'Pick an answer above, or type your own…';
}
